import React from 'react';
import { Button, DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

const chipBaseStyle = {
  minWidth: '52px',
  padding: '4px 10px',
  borderRadius: '12px',
  borderWidth: '1.5px',
  borderStyle: 'solid',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  userSelect: 'none' as const,
  appearance: 'none' as const,
  WebkitAppearance: 'none' as const,
  boxShadow: 'none',
  outline: 'none',
  backgroundClip: 'padding-box' as const,
};

interface PredictionToolbarProps {
  displayRangeEndText: string;
  quickRangeDays: number[];
  panelOpen: boolean;
  predicting: boolean;
  panelSelectionDate: Dayjs;
  panelSelectionDays: number;
  panelSelectionMode: 'preset' | 'custom';
  startDateText: string;
  lastPredictedAt?: string;
  onTogglePanel: () => void;
  onQuickRangeChange: (days: number) => void;
  onEndDateChange: (value: Dayjs | null) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const PredictionToolbar: React.FC<PredictionToolbarProps> = ({
  displayRangeEndText,
  quickRangeDays,
  panelOpen,
  predicting,
  panelSelectionDate,
  panelSelectionDays,
  panelSelectionMode,
  startDateText,
  lastPredictedAt,
  onTogglePanel,
  onQuickRangeChange,
  onEndDateChange,
  onCancel,
  onConfirm,
}) => {
  const isCustomMode = panelSelectionMode === 'custom';
  const rangeSummaryText = `${startDateText}（今天）~ ${displayRangeEndText}`;

  return (
    <section className="mb-[10px] flex flex-col gap-2 rounded-[8px] bg-[#f9fafb] px-3 py-2 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
        <div className="flex flex-wrap items-center gap-[6px] text-[11.5px] text-[#0f172a]">
          <span className="mr-[2px] font-medium text-[#6b7280]">预测窗口</span>
          <span className="font-medium text-[#0f172a]">{rangeSummaryText}</span>
        </div>
        <div className="hidden h-[18px] w-px bg-[#e5e7eb] xl:block" />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-[10px]">
        <div className="text-[11px] text-[#94a3b8]">
          最近预测：
          <span className="font-medium text-[#6b7280]">
            {lastPredictedAt || '暂无'}
          </span>
        </div>
        <div className="relative">
          <button
            type="button"
            disabled={predicting}
            onClick={onTogglePanel}
            className="inline-flex h-[32px] leading-[30px] items-center gap-1 rounded-[7px] border border-solid border-[#2563eb] bg-[#2563eb] pr-3 pl-2 pt-0.5 text-[12px] font-medium cursor-pointer text-white shadow-[0_1px_2px_rgba(37,99,235,0.18)] transition-colors hover:border-[#1d4ed8] hover:bg-[#1d4ed8] disabled:cursor-not-allowed disabled:border-[#93c5fd] disabled:bg-[#93c5fd] disabled:shadow-none"
          >
            {predicting ? '⏳ 预测中...' : '🔄 立即预测'}
          </button>

          {panelOpen ? (
            <div className="absolute right-0 top-[calc(100%+8px)] z-20 w-[420px] rounded-[12px] border border-[#dbe4f0] bg-white px-[24px] py-[22px] shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
              <div className="mb-[18px] text-[12px] font-semibold leading-[18px] text-[#0f172a]">
                选择预测截至日期
              </div>

              <div className="mb-[18px] grid items-center grid-cols-[78px_minmax(0,1fr)] gap-x-[10px] gap-y-[10px] text-[12px] text-[#475569]">
                <div className="whitespace-nowrap text-[14px] leading-[22px] text-[#6b7280]">
                  起始时间：
                </div>
                <div className="text-[14px] font-medium leading-[22px] text-[#0f172a]">
                  {startDateText}（今天）
                </div>
                <div className="whitespace-nowrap text-[14px] leading-[22px] text-[#6b7280]">
                  截止时间：
                </div>
                <div className="w-[58%] min-w-[170px] max-w-[218px]">
                  <DatePicker
                    value={panelSelectionDate}
                    allowClear={false}
                    format="YYYY-MM-DD"
                    className="!h-[32px] !w-full !rounded-[6px] [&_.ant-picker-input>input]:!text-[14px] [&_.ant-picker-input>input]:!font-medium [&_.ant-picker-input>input]:!leading-[22px] [&_.ant-picker-input>input]:!text-[#1f2937] [&_.ant-picker-suffix]:!text-[#9ca3af]"
                    disabledDate={current =>
                      Boolean(
                        current &&
                          (current
                            .endOf('day')
                            .isBefore(dayjs().add(7, 'day').startOf('day')) ||
                            current
                              .startOf('day')
                              .isAfter(dayjs().add(180, 'day').startOf('day')))
                      )
                    }
                    onChange={onEndDateChange}
                  />
                </div>
                <div />
                <div className="flex flex-wrap items-center gap-[12px] pt-[2px]">
                  {quickRangeDays.map(days => (
                    <div
                      key={days}
                      role="button"
                      tabIndex={0}
                      className="transition-colors hover:border-[#9ca3af]"
                      style={{
                        ...chipBaseStyle,
                        borderColor:
                          !isCustomMode && panelSelectionDays === days
                            ? '#1f2342'
                            : '#e5e7eb',
                        backgroundColor:
                          !isCustomMode && panelSelectionDays === days
                            ? '#1f2342'
                            : '#ffffff',
                        color:
                          !isCustomMode && panelSelectionDays === days
                            ? '#ffffff'
                            : '#1f2937',
                      }}
                      onClick={() => onQuickRangeChange(days)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onQuickRangeChange(days);
                        }
                      }}
                    >
                      未来 {days / 30} 月
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  size="small"
                  className="!h-7 !rounded-[6px] !px-[10px] !text-[12px] !leading-[18px]"
                  onClick={onCancel}
                >
                  取消
                </Button>
                <Button
                  size="small"
                  type="primary"
                  className="!h-7 !rounded-[6px] !px-[10px] !text-[12px] !leading-[18px]"
                  onClick={onConfirm}
                >
                  确定
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default PredictionToolbar;
